import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, PlusCircle, TrendingUp, ClipboardList, Megaphone, Package, ArrowRight } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { ConexionAPI, TIPO_META, tituloDashboardPorRol, filtroInicioPorRol } from "../../lib/conexion";
import PublicacionCard from "./PublicacionCard";

function StatChip({ Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-4 flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}18`, color }}>
        <Icon size={20} />
      </div>
      <div className="min-w-0">
        <p className="text-lg font-extrabold text-slate-900 leading-none">{value}</p>
        <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mt-1 leading-tight">{label}</p>
      </div>
    </div>
  );
}

export default function ConexionDashboard() {
  const { user } = useAuth();
  const [publicaciones, setPublicaciones] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [tipoFiltro, setTipoFiltro] = useState(null);

  const rol = user?.role || "cliente";
  const filtroBase = useMemo(() => filtroInicioPorRol(rol), [rol]);

  async function cargar(forzarTipo = null) {
    try {
      setRefreshing(true);
      const filtros = forzarTipo ? { tipo: forzarTipo, limite: 50 } : { ...filtroBase, limite: 50 };
      const [pubs, est] = await Promise.all([
        ConexionAPI.listarPublicaciones(filtros),
        ConexionAPI.estadisticas(),
      ]);
      setPublicaciones(pubs);
      setStats(est);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    cargar(tipoFiltro);
  }, [tipoFiltro, rol, filtroBase]);

  const chipsTipos = [
    { key: null, label: "Todos" },
    { key: "solicitud", label: "Cliente" },
    { key: "oferta", label: "Maestro-Contratista" },
    { key: "producto", label: "Ferretería" },
  ];

  return (
    <div className="space-y-6" data-testid="conexion-dashboard">
      {/* Hero saludo */}
      <section className="rounded-2xl border border-slate-100 shadow-soft bg-white p-6 md:p-7 flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-red">
            Hola{user?.name ? `, ${user.name.split(" ")[0]}` : ""}
          </p>
          <h2 className="mt-1 text-2xl font-extrabold text-slate-900 tracking-tight">
            {tituloDashboardPorRol(rol)}
          </h2>
          <p className="text-sm text-slate-500 mt-1">Publicaciones activas en todo el Perú, actualizadas al momento.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => cargar(tipoFiltro)}
            data-testid="conexion-refresh"
            className="inline-flex items-center gap-2 border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} /> Refrescar
          </button>
          <Link
            to="/conexion/publicar"
            data-testid="conexion-publicar-cta"
            className="inline-flex items-center gap-2 bg-brand-red hover:bg-brand-red-hover text-white font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors shadow-sm"
          >
            <PlusCircle size={16} /> Publicar
          </Link>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="conexion-stats">
          <StatChip Icon={TrendingUp} label="Publicaciones activas" value={stats.publicaciones_totales} color="#1E3A8A" />
          <StatChip Icon={ClipboardList} label="Cliente" value={stats.solicitudes} color="#DC2626" />
          <StatChip Icon={Megaphone} label="Maestro-Contratista" value={stats.ofertas} color="#16A34A" />
          <StatChip Icon={Package} label="Ferretería" value={stats.productos} color="#D97706" />
        </section>
      )}

      {/* Chips tipo */}
      <div className="flex flex-wrap gap-2">
        {chipsTipos.map((c) => {
          const active = c.key === tipoFiltro;
          const color = c.key ? TIPO_META[c.key].color : "#1E3A8A";
          return (
            <button
              key={c.label}
              onClick={() => setTipoFiltro(c.key)}
              data-testid={`conexion-chip-${c.label.toLowerCase()}`}
              className={[
                "px-4 py-1.5 rounded-full text-sm font-semibold border transition-all",
                active
                  ? "text-white border-transparent shadow-sm"
                  : "text-slate-600 bg-white border-slate-200 hover:border-slate-300 hover:bg-slate-50",
              ].join(" ")}
              style={active ? { backgroundColor: color } : {}}
            >
              {c.label}
            </button>
          );
        })}
      </div>

      {/* Grid publicaciones */}
      {loading ? (
        <div className="text-center py-16 text-slate-500 text-sm">Cargando publicaciones…</div>
      ) : publicaciones.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center">
          <p className="text-slate-600 font-semibold">Aún no hay publicaciones en este filtro.</p>
          <Link to="/conexion/publicar" className="mt-4 inline-flex items-center gap-2 text-brand-red font-semibold hover:underline text-sm">
            Publica la primera <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="conexion-feed">
          {publicaciones.map((p) => (
            <PublicacionCard key={p.id} pub={p} />
          ))}
        </div>
      )}
    </div>
  );
}
